import React, { useMemo } from "react";
// ANT DESIGN COMPONENTS
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import { updateMapViewItem } from "../../../redux/mapView/mapViewSlice";
import {
  updateLiftSelected,
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateControlPanelDrawerPlacement,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
import MonitoringMapViewPin3 from "./MonitoringMapViewPin3";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
// INTERFACES
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { MapViewDirections, MapViewTypes } from "../../../interfaces/MapView.interface";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import { defaultLiftPanelTab } from "../../../types/monitoringAccess";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { stringToUuidT } from "../../../utils/uuid";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import elClose from "../../../assets/icons/elevatorSingle/el-close.svg";
import elOpen from "../../../assets/icons/elevatorSingle/el-open.svg";
import elOpening from "../../../assets/icons/elevatorSingle/el-opening.svg";
import elUp from "../../../assets/icons/elevatorStatus/el-up.svg";
import elDown from "../../../assets/icons/elevatorStatus/el-down.svg";
import elFrontOpenRearOpen from "../../../assets/icons/elevatorDouble/el-front-open-rear-open.svg";
import elFrontCloseRearClose from "../../../assets/icons/elevatorDouble/el-front-close-rear-close.svg";
import elFrontOpenRearClose from "../../../assets/icons/elevatorDouble/el-front-open-rear-close.svg";
import elFrontCloseRearOpen from "../../../assets/icons/elevatorDouble/el-front-close-rear-open.svg";
import elFrontOpeningRearClose from "../../../assets/icons/elevatorDouble/el-front-opening-rear-close.svg";
import elFrontOpenRearOpening from "../../../assets/icons/elevatorDouble/el-front-open-rear-opening.svg";
import elFrontCloseRearOpening from "../../../assets/icons/elevatorDouble/el-front-close-rear-opening.svg";
import elFrontOpeningRearOpen from "../../../assets/icons/elevatorDouble/el-front-opening-rear-open.svg";
import elFrontOpeningRearOpening from "../../../assets/icons/elevatorDouble/el-front-opening-rear-opening.svg";
import esStopped from "../../../assets/icons/escalator/es-dark-stopped.svg";
import weStopped from "../../../assets/icons/movingWalk/we-dark-stopped.svg";
import { usageToDoorIconFiltered } from "../../../utils/bitmaskToIcon";

function toRgba(hexColor: string, opacityPercent: number): string {
  const sanitized = (hexColor || "").replace("#", "").trim();
  const alpha = Math.min(Math.max(opacityPercent, 0), 100) / 100;

  if (!sanitized || alpha <= 0) {
    return "rgba(0, 0, 0, 0)";
  }

  let expanded = sanitized;
  if (sanitized.length === 3) {
    expanded = sanitized
      .split("")
      .map((char) => `${char}${char}`)
      .join("");
  }

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    return "rgba(0, 0, 0, 0)";
  }

  const r = Number.parseInt(expanded.slice(0, 2), 16);
  const g = Number.parseInt(expanded.slice(2, 4), 16);
  const b = Number.parseInt(expanded.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function resolveIconSrc(icon: string, liftType: string): string {
  if (icon === "elUp") {
    return elUp;
  }
  if (icon === "elDown") {
    return elDown;
  }
  if (icon === "elOpen") {
    return elOpen;
  }
  if (icon === "elClose") {
    return elClose;
  }
  if (icon === "elOpening") {
    return elOpening;
  }
  if (icon === "elFrontOpenRearOpen") {
    return elFrontOpenRearOpen;
  }
  if (icon === "elFrontCloseRearClose") {
    return elFrontCloseRearClose;
  }
  if (icon === "elFrontOpenRearClose") {
    return elFrontOpenRearClose;
  }
  if (icon === "elFrontCloseRearOpen") {
    return elFrontCloseRearOpen;
  }
  if (icon === "elFrontOpeningRearClose") {
    return elFrontOpeningRearClose;
  }
  if (icon === "elFrontOpenRearOpening") {
    return elFrontOpenRearOpening;
  }
  if (icon === "elFrontCloseRearOpening") {
    return elFrontCloseRearOpening;
  }
  if (icon === "elFrontOpeningRearOpen") {
    return elFrontOpeningRearOpen;
  }
  if (icon === "elFrontOpeningRearOpening") {
    return elFrontOpeningRearOpening;
  }
  if (icon === "esStopped") {
    return esStopped;
  }
  if (icon === "weStopped") {
    return weStopped;
  }

  const normalizedType = liftType.toLowerCase();
  if (normalizedType === "escalator") {
    return esStopped;
  }
  if (normalizedType === "movingwalk") {
    return weStopped;
  }

  return elClose;
}

function getDefaultIconForLiftType(liftType: string): string {
  const normalized = liftType.toLowerCase();
  if (normalized === "escalator") {
    return "esStopped";
  }
  if (normalized === "movingwalk") {
    return "weStopped";
  }
  return "elClose";
}

function getLiftDoorConfiguration(
  liftId: string,
  layouts: any[],
): { frontDoors: boolean; rearDoors: boolean; bankHasMixedDoors: boolean } {
  const matchingLayout = layouts.find(
    (layout: any) =>
      Array.isArray(layout?.lifts) &&
      layout.lifts.some((lift: any) => String(lift?.id ?? "") === String(liftId)),
  );

  const matchingLift = matchingLayout?.lifts?.find(
    (lift: any) => String(lift?.id ?? "") === String(liftId),
  );

  const frontDoors = Boolean(
    matchingLift?.frontDoor ?? matchingLift?.frontDoors ?? matchingLayout?.frontDoors ?? true,
  );
  const rearDoors = Boolean(
    matchingLift?.rearDoor ?? matchingLift?.rearDoors ?? matchingLayout?.rearDoors ?? false,
  );

  return {
    frontDoors,
    rearDoors,
    bankHasMixedDoors: frontDoors && rearDoors,
  };
}

function resolveLiftIcon(
  liftType: string,
  socketMessageLift: SocketMessageInterface,
  hasLiveMessage: boolean,
  liftId: string,
  layouts: any[],
): string {
  const normalized = liftType.toLowerCase();

  if (normalized === "escalator" || normalized === "movingwalk") {
    return getDefaultIconForLiftType(liftType);
  }

  if (!hasLiveMessage) {
    return getDefaultIconForLiftType(liftType);
  }

  const doorConfig = getLiftDoorConfiguration(liftId, layouts);
  const isMoving =
    socketMessageLift.direction === Usage.Up || socketMessageLift.direction === Usage.Down;

  if (isMoving) {
    if (socketMessageLift.direction === Usage.Up) {
      return "elUp";
    }
    if (socketMessageLift.direction === Usage.Down) {
      return "elDown";
    }
  }

  const doorIcon = usageToDoorIconFiltered(
    socketMessageLift.usage ?? {},
    doorConfig.frontDoors,
    doorConfig.rearDoors,
    undefined,
    doorConfig.bankHasMixedDoors,
  );

  if (doorIcon) {
    return doorIcon;
  }

  return getDefaultIconForLiftType(liftType);
}

function MonitoringMapViewWrapper({
  liftId,
  direction,
  text,
  x,
  y,
  type,
  layouts,
  liftType,
  interactive = true,
  displayName = true,
  scale = 1,
  backgroundColorHex = "",
  backgroundOpacity = 0,
  titleFontSize = 10,
  titleColorHex = "#000000",
  titleRotationDeg = 0,
  backgroundWidth = 50,
  backgroundHeight = 50,
}: {
  liftId: string;
  direction: MapViewDirections;
  text: string;
  x: number;
  y: number;
  type: MapViewTypes;
  layouts: any;
  liftType: string;
  interactive?: boolean;
  displayName?: boolean;
  scale?: number;
  backgroundColorHex?: string;
  backgroundOpacity?: number;
  titleFontSize?: number;
  titleColorHex?: string;
  titleRotationDeg?: number;
  backgroundWidth?: number;
  backgroundHeight?: number;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [zIndex, setZIndex] = React.useState(0);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { mapViewArray } = useSelector((state: RootState) => state.mapView);
  const { permissionSet } = useSelector((state: RootState) => state.user);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(liftId), [liftId]);
  const latestLiftMessage = useLastLiftMessage(liftUuid);
  const socketMessageLift: SocketMessageInterface = latestLiftMessage || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = socketMessageLift.faults;
  const icon = useMemo(
    () => resolveLiftIcon(liftType, socketMessageLift, Boolean(latestLiftMessage), liftId, layouts),
    [liftType, latestLiftMessage, socketMessageLift, liftId, layouts],
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getTabKey = () => {
    // Actions is the default tab for the drawer whenever it's available.
    if (hasPermission(Permission.MonitorControlPanelActions, permissionSet ?? [])) {
      return "ACTIONS";
    }
    if (hasFaults) {
      return "FAULTS";
    }
    return defaultLiftPanelTab(permissionSet ?? []);
  };

  const getLayoutAndLift = () => {
    const selectedLayout = layouts.find((layout: any) =>
      layout?.lifts.find((lift_: any) => lift_.id === liftId),
    );
    const selectedLift = selectedLayout?.lifts.find((lift_: any) => lift_.id === liftId);
    return { selectedLayout, selectedLift };
  };

  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  const onClickOpenModalElevator = (e: any) => {
    getDrawerPlacement(e);
    const { selectedLayout, selectedLift } = getLayoutAndLift();
    if (!selectedLayout || !selectedLift) return;
    dispatch(
      updateModalControlPanelTab({
        liftType: LiftType[LiftType.ELEVATOR],
        tab: getTabKey(),
      }),
    );
    dispatch(updateLiftSelected(selectedLift));
    dispatch(updateChannelId(selectedLayout.idChannel));
    dispatch(updateServerId(selectedLayout.idServer));
    dispatch(updateOpenModalControlPanelLift(true));
  };

  const handleClick = (e: any) => {
    if (type === MapViewTypes.MAP) {
      // get the selected map view data
      const selectedMapView = mapViewArray.find((mapView: any) => mapView.id === liftId);
      if (selectedMapView) {
        // update my element
        dispatch(updateMapViewItem(selectedMapView));
      }
    } else {
      onClickOpenModalElevator(e);
    }
  };

  const getDirection: () => "top" | "bottom" | "left" | "right" = () => {
    if (direction === MapViewDirections.UP) return "top";
    if (direction === MapViewDirections.DOWN) return "bottom";
    if (direction === MapViewDirections.LEFT) return "left";
    if (direction === MapViewDirections.RIGHT) return "right";
    return "top";
  };

  const isVertical = direction === MapViewDirections.UP || direction === MapViewDirections.DOWN;

  // ************************************************ */
  // COMPONENT ************************************** */
  const wrapperStyle: React.CSSProperties = {
    position: "absolute",
    top: `${y}px`,
    left: `${x}px`,
    zIndex: interactive ? zIndex : 24,
    pointerEvents: interactive ? "auto" : "none",
  };

  const pinContainerStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    transform: `translate(-50%, -50%) scale(${scale})`,
    transformOrigin: "center center",
  };

  const titleOffset = Math.max(Math.max(backgroundHeight, backgroundWidth) / 2 + 6, 8);
  const titleStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    transform: `translate(-50%, -100%) rotate(${titleRotationDeg}deg) translateY(-${titleOffset}px)`,
    transformOrigin: "center center",
    color: titleColorHex,
    fontSize: `${Math.max(8, titleFontSize)}px`,
    whiteSpace: "nowrap",
    pointerEvents: "none",
  };

  const pinNode = (
    <MonitoringMapViewPin3
      carName={displayName ? text : ""}
      icon={icon}
      direction={getDirection()}
      orientation={isVertical ? "vertical" : "horizontal"}
      liftId={liftId}
      liftType={liftType}
      mapBackgroundColorHex={backgroundColorHex}
      mapBackgroundOpacity={backgroundOpacity}
      forceDisplayName={displayName}
      forceHideName
      mapTitleFontSize={titleFontSize}
      mapTitleColorHex={titleColorHex}
      mapTitleRotationDeg={titleRotationDeg}
    />
  );

  if (!interactive) {
    const liftWidth = Math.max(8, backgroundWidth);
    const liftHeight = Math.max(8, backgroundHeight);
    const iconSize = Math.round(Math.min(liftWidth, liftHeight) * 0.8);
    const iconSrc = resolveIconSrc(icon, liftType);
    const isElevator = liftType.toLowerCase() === "elevator";
    const floorFontSize = Math.max(8, Math.ceil(titleFontSize * 0.8));
    const floorGap = Math.max(3, Math.ceil(floorFontSize * 0.3));
    const floorTopOffset = Math.round(liftHeight / 2 + floorGap);
    const floorLabel =
      socketMessageLift.position === null || socketMessageLift.position === undefined
        ? "--"
        : String(socketMessageLift.position);

    return (
      <div style={wrapperStyle} aria-label="map view wrapper">
        {displayName && text ? <div style={titleStyle}>{text}</div> : null}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: liftWidth,
            height: liftHeight,
            transform: "translate(-50%, -50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            borderRadius: 4,
            backgroundColor: toRgba(backgroundColorHex, backgroundOpacity),
            opacity: 1,
          }}
        >
          <img
            src={iconSrc}
            alt="Lift waypoint"
            style={{
              width: iconSize,
              height: iconSize,
              objectFit: "contain",
              display: "block",
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.45))",
            }}
          />
        </div>
        {isElevator ? (
          <div
            style={{
              position: "absolute",
              left: 0,
              top: floorTopOffset,
              transform: "translateX(-50%)",
              whiteSpace: "nowrap",
              fontSize: floorFontSize,
              lineHeight: 1.2,
              fontWeight: 600,
              color: "#111111",
              textShadow: "0 1px 2px rgba(255,255,255,0.85)",
              pointerEvents: "none",
            }}
          >
            {`Floor: ${floorLabel}`}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={wrapperStyle}
      onClick={handleClick}
      onMouseEnter={() => setZIndex(99999)}
      onMouseLeave={() => setZIndex(0)}
      role="button"
      tabIndex={0}
      aria-label="map view wrapper"
      onKeyDown={handleClick}
    >
      {displayName && text ? <div style={titleStyle}>{text}</div> : null}
      <div style={pinContainerStyle}>{pinNode}</div>
    </div>
  );
}

export default MonitoringMapViewWrapper;
